import styles from "./MultiSelectFieldInput.module.scss";
import cx from "classnames";
import { PassThroughProps, Size } from "../../../../types";
import { Button } from "../../../../components/Button";
import { Chip } from "../../../../components/Chip";
import { Flex } from "../../../../components/Flex";
import { mergeRefs } from "../../../../hooks/useMergeRefs/useMergeRefs";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import {
  ReactElement,
  RefObject,
  useRef,
  KeyboardEvent,
  MouseEvent,
} from "react";
import { MultiSelectFieldChipProps, MultiSelectFieldOption } from "../types";
import { useChipLayout } from "./useChipLayout";

/**
 * Props for the MultiSelectFieldInput component
 * @property {string} [placeholder] - Placeholder text displayed when input is empty
 * @property {Extract<Size, "small" | "medium" | "large">} [size] - Size variant of the input
 * @property {boolean} [disableClearButton] - Hides the clear button when true
 * @property {boolean} [disableToggleButton] - Hides the toggle button when true
 * @property {boolean} [disabled] - Disables the input entirely
 * @property {boolean} [readOnly] - Cannot be modified but remains interactive
 * @property {boolean} [error] - Shows error styling on the input when true
 * @property {() => void} [onClear] - Callback fired when the clear button is clicked
 * @property {string} [id] - HTML id attribute for the input element
 * @property {RefObject<HTMLDivElement>} [inputWrapperRef] - Ref for the input wrapper div element
 * @property {PassThroughProps<"div">} [htmlInputWrapperProps] - Additional props passed to the wrapper div
 * @property {PassThroughProps<"input">} [inputProps] - Additional props passed to the input element
 * @property {PassThroughProps<"button">} [toggleButtonProps] - Additional props passed to the toggle button
 * @property {string | ReactElement} [prefix] - Content to display before the input
 * @property {string | ReactElement} [suffix] - Content to display after the input
 * @property {MultiSelectFieldOption[]} [selectedOptions] - Array of selected options to display as chips
 * @property {(option: MultiSelectFieldOption) => void} [onRemoveOption] - Callback when a chip is removed
 * @property {boolean} [singleRow] - When true, restricts the field to a single row height
 * @property {number} [maxChips] - Maximum number of chips to display before showing +N indicator (default: 10)
 */
export type MultiSelectFieldInputProps = {
  placeholder?: string;
  size?: Extract<Size, "small" | "medium" | "large">;
  disableClearButton?: boolean;
  disableToggleButton?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  onClear?: () => void;
  id?: string;
  inputWrapperRef?: RefObject<HTMLDivElement>;
  htmlInputWrapperProps?: PassThroughProps<"div">;
  inputProps?: PassThroughProps<"input">;
  toggleButtonProps?: PassThroughProps<"button">;
  prefix?: string | ReactElement;
  suffix?: string | ReactElement;
  selectedOptions?: MultiSelectFieldOption[];
  onRemoveOption?: (option: MultiSelectFieldOption) => void;
  singleRow?: boolean;
  maxChips?: number;
  getChipProps?: (option: MultiSelectFieldOption) => MultiSelectFieldChipProps;
  onWrapperClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

/**
 * Internal input component for the MultiSelectField that provides the text input,
 * chip display for selected options, and action buttons.
 *
 * Features:
 * - Text input for filtering options
 * - Chip display for selected options with remove functionality
 * - Optional clear button for removing all selections
 * - Optional toggle button for opening/closing the dropdown menu
 * - Supports multiple size variants (small, medium, large)
 * - Fully accessible with proper ARIA labels on buttons
 * - Supports pass-through props for customization of internal elements
 * - Optional prefix and suffix content
 * - Error state styling
 * - Single row mode with singleRow prop
 * - Chip count limiting with maxChips prop
 *
 * @example
 * <MultiSelectFieldInput
 *   placeholder="Search..."
 *   size="medium"
 *   selectedOptions={[{ id: 1, label: "Option 1" }]}
 *   onRemoveOption={(option) => handleRemove(option)}
 *   onClear={() => handleClearAll()}
 * />
 */
export const MultiSelectFieldInput = ({
  placeholder,
  size,
  disableClearButton,
  disableToggleButton,
  disabled,
  readOnly,
  error,
  onClear,
  id,
  inputWrapperRef,
  htmlInputWrapperProps,
  inputProps,
  toggleButtonProps,
  prefix,
  suffix,
  selectedOptions = [],
  onRemoveOption,
  singleRow,
  maxChips,
  getChipProps,
  onWrapperClick,
}: MultiSelectFieldInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const externalRef = (inputProps as { ref?: React.Ref<HTMLInputElement> })
    ?.ref;
  const mergedInputRef = mergeRefs([inputRef, externalRef]);

  const { rowsRef, displayCount, hiddenCount } = useChipLayout({
    selectedOptionsLength: selectedOptions.length,
    singleRow,
    maxChips,
  });

  const inputWrapperClassNames = cx(styles["input-wrapper"], {
    [styles["small"]]: size === "small",
    [styles["large"]]: size === "large",
    [styles["no-clear-button"]]: disableClearButton,
    [styles["no-toggle-button"]]: disableToggleButton,
    [styles["disabled"]]: disabled || readOnly,
  });

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to remove the last chip when input is empty
    if (
      e.key === "Backspace" &&
      !inputProps?.value &&
      selectedOptions.length > 0 &&
      onRemoveOption &&
      !disabled &&
      !readOnly
    ) {
      onRemoveOption(selectedOptions[selectedOptions.length - 1]);
    }

    // Call the original onKeyDown if provided
    if (inputProps?.onKeyDown) {
      (inputProps.onKeyDown as (e: KeyboardEvent<HTMLInputElement>) => void)(e);
    }
  };

  const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
    // Skip if clicking on interactive elements (buttons) or input
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.tagName === "INPUT") {
      return;
    }
    onWrapperClick?.(e);
    inputRef.current?.focus();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- Click-to-focus container; Despite the disabled rules, this remains accessible. Keyboard interaction is provided by the input and nested buttons are filtered out in the handler.
    <div
      ref={inputWrapperRef}
      {...htmlInputWrapperProps}
      className={cx(inputWrapperClassNames, htmlInputWrapperProps?.className)}
      onClick={handleWrapperClick}
    >
      {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}

      <div className={styles["rows-wrapper"]} ref={rowsRef}>
        {selectedOptions.length > 0 && onRemoveOption
          ? selectedOptions.slice(0, displayCount).map((option, index) => (
              <div
                key={`selected-item-${option.id}-${index}`}
                className={styles["chip-wrapper"]}
              >
                <Chip
                  {...getChipProps?.(option)}
                  label={option.label}
                  onClose={
                    disabled || readOnly
                      ? undefined
                      : () => onRemoveOption(option)
                  }
                  className={styles["chip"]}
                  title={option.label}
                />
              </div>
            ))
          : null}

        {hiddenCount > 0 ? (
          <div className={styles["chip-wrapper"]}>
            <Chip
              label={`+${hiddenCount}`}
              className={styles["chip"]}
              title={selectedOptions
                .slice(displayCount)
                .map((option) => option.label)
                .join(", ")}
            />
          </div>
        ) : null}

        <Flex className={styles["input-flex"]} alignItems="center">
          <input
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            {...inputProps}
            ref={mergedInputRef}
            onKeyDown={handleInputKeyDown}
            className={cx(styles["input"], inputProps?.className, {
              [styles["error"]]: error,
            })}
          />
          {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
        </Flex>
      </div>

      <div className={styles["buttons-wrapper"]}>
        {disableClearButton ? null : (
          <div className={styles["close-button-wrapper"]}>
            <Button
              icon={Close}
              aria-label="clear selection"
              appearance="ghost"
              size="small"
              className={styles["close-button"]}
              onClick={() => {
                onClear?.();
              }}
            />
          </div>
        )}
        {disableToggleButton ? null : (
          <div className={styles["toggle-button-wrapper"]}>
            <Button
              icon={Chevron_Right}
              aria-label="toggle menu"
              appearance="ghost"
              size="small"
              disabled={disabled}
              {...toggleButtonProps}
              className={cx(
                styles["toggle-button"],
                toggleButtonProps?.className,
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

MultiSelectFieldInput.displayName = "MultiSelectFieldInput";
